import { useState, useEffect } from 'react';
import { getSettings, upsertSetting } from '../../utils/api';

// Kids Settings — email templates for the emails the Kids team sends.
// These are KIDS-OWNED templates (kids-* ids): editing them never touches the
// main app's templates on Kat's Settings page, and vice versa. Same editor
// pattern as the main Settings tab.

const KIDS_TEMPLATES = [
  {
    id: 'kids-availability-request',
    name: 'Availability Request',
    meta: 'Sent by "Request Availability" on the People tab',
    trigger: 'Triggered when: you email the roster asking for their serving plans (most-helpful-3 form)',
    defaultSubject: 'Tell us your Kids Conference plans, {{first_name}}!',
    defaultBody: "Hi {{first_name}},\n\nWe're building the Kids Conference serving schedule and we'd love to know your plans. Pick how many sessions you can serve, then check the ones you're free for — it takes about a minute.\n\n{{availability_button}}\n\nThank you for serving our kids!\n\n— The Belonging Co Kids Team",
    vars: ['{{first_name}}', '{{team_name}}', '{{availability_button}}', '{{availability_link}}', '{{year}}'],
  },
  {
    id: 'kids-schedule-link',
    name: 'Schedule Link',
    meta: 'Sent by "Send Schedule Links" + the per-person send button',
    trigger: "Triggered when: you send someone their personal schedule link",
    defaultSubject: 'Your Kids Conference schedule is ready, {{first_name}}',
    defaultBody: 'Hi {{first_name}},\n\nYour Kids Conference serving schedule is ready. You can view it anytime — no login needed, and it always stays up to date.\n\n{{schedule_link_button}}\n\nThank you for serving our kids — we could not do this without you!\n\n— The Belonging Co Kids Team',
    vars: ['{{first_name}}', '{{schedule_link_button}}', '{{schedule_link}}', '{{year}}'],
  },
  {
    id: 'kids-schedule-assignment',
    name: 'Assignment Request',
    meta: 'Sent by "Assign & Send Request" when filling a position',
    trigger: 'Triggered when: you assign someone to a block and send the request',
    defaultSubject: "You've been scheduled for {{block_name}}, {{first_name}}",
    defaultBody: 'Hi {{first_name}},\n\nThe Kids team has scheduled you for {{block_name}}. Take a look and confirm using your personal link.\n\n{{schedule_link_button}}\n\nThank you for serving our kids!\n\n— The Belonging Co Kids Team',
    vars: ['{{first_name}}', '{{block_name}}', '{{team_lead_name}}', '{{schedule_link_button}}', '{{year}}'],
  },
];

export default function KidsSettings({ showToast, readOnly }) {
  const [selectedId, setSelectedId] = useState(KIDS_TEMPLATES[0].id);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const tpl = KIDS_TEMPLATES.find(t => t.id === selectedId);

  useEffect(() => {
    getSettings()
      .then(s => {
        const o = {};
        KIDS_TEMPLATES.forEach(t => {
          o[t.id] = {
            subject: s?.[`template.${t.id}.subject`] || '',
            body: s?.[`template.${t.id}.body`] || '',
          };
        });
        setOverrides(o);
      })
      .catch(() => showToast('Could not load templates.'))
      .finally(() => setLoading(false));
  }, [showToast]);

  // Load the selected template's current values (override or default).
  useEffect(() => {
    if (loading || !tpl) return;
    const o = overrides[tpl.id] || {};
    setSubject(o.subject || tpl.defaultSubject);
    setBody(o.body || tpl.defaultBody);
  }, [selectedId, loading, overrides, tpl]);

  async function handleSave() {
    if (!tpl) return;
    setSaving(true);
    try {
      await Promise.all([
        upsertSetting(`template.${tpl.id}.subject`, subject),
        upsertSetting(`template.${tpl.id}.body`, body),
      ]);
      setOverrides(prev => ({ ...prev, [tpl.id]: { subject, body } }));
      showToast('Template saved.');
    } catch (e) {
      showToast('Error saving template.');
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    if (!tpl) return;
    setSaving(true);
    try {
      await Promise.all([
        upsertSetting(`template.${tpl.id}.subject`, ''),
        upsertSetting(`template.${tpl.id}.body`, ''),
      ]);
      setOverrides(prev => ({ ...prev, [tpl.id]: { subject: '', body: '' } }));
      setSubject(tpl.defaultSubject);
      setBody(tpl.defaultBody);
      showToast('Back to the default wording.');
    } catch (e) {
      showToast('Error resetting template.');
    } finally {
      setSaving(false);
    }
  }

  function insertVar(v) {
    setBody(b => `${b}${b.endsWith('\n') || b === '' ? '' : ' '}${v}`);
  }

  const edited = tpl && !!(overrides[tpl.id]?.subject || overrides[tpl.id]?.body);

  return (
    <div className="page-content">
      <div className="page-title-row">
        <div>
          <div className="page-title">Settings</div>
          <div className="page-sub">Email templates for the Kids team's emails — separate from the main app's templates</div>
        </div>
      </div>

      {loading ? (
        <div className="panel" style={{ padding: '32px 24px', textAlign: 'center', color: 'var(--muted)', fontFamily: 'var(--font-b)', fontWeight: 300 }}>Loading…</div>
      ) : (
        <div className="two-col" style={{ gridTemplateColumns: '260px 1fr', alignItems: 'start' }}>
          <div>
            <div className="section-header" style={{ marginBottom: 10 }}>All Templates</div>
            <div className="panel">
              {KIDS_TEMPLATES.map(t => (
                <div key={t.id} className={`email-tpl-item${t.id === selectedId ? ' active' : ''}`} onClick={() => setSelectedId(t.id)}>
                  <div className="email-tpl-name">
                    {t.name}
                    {(overrides[t.id]?.subject || overrides[t.id]?.body) ? <span className="pill pill-gray" style={{ fontSize: 9, marginLeft: 6 }}>Edited</span> : null}
                  </div>
                  <div className="email-tpl-meta">{t.meta}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="panel" style={{ padding: 24 }}>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontFamily: 'var(--font-h)', fontSize: 17, fontWeight: 800, color: 'var(--black)' }}>{tpl.name}</div>
              <div style={{ fontFamily: 'var(--font-b)', fontSize: 13, fontWeight: 300, color: 'var(--muted)', marginTop: 3 }}>{tpl.trigger}</div>
            </div>

            <div className="form-group">
              <label className="form-label">Subject</label>
              <input className="form-input" value={subject} onChange={e => setSubject(e.target.value)} disabled={readOnly} />
            </div>
            <div className="form-group">
              <label className="form-label">Body</label>
              <textarea
                className="form-input"
                rows={11}
                value={body}
                onChange={e => setBody(e.target.value)}
                disabled={readOnly}
                style={{ resize: 'vertical', fontFamily: 'var(--font-b)', fontWeight: 300, lineHeight: 1.6 }}
              />
            </div>

            <div style={{ marginBottom: 18 }}>
              <div style={{ fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 8 }}>
                Available Variables — click to add
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {tpl.vars.map(v => (
                  <button key={v} className="tpl-token" onClick={() => insertVar(v)} disabled={readOnly}>{v}</button>
                ))}
              </div>
            </div>

            {!readOnly && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button className="btn btn-dark" onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save Changes'}</button>
                {edited && <button className="btn" onClick={handleReset} disabled={saving}>Reset to Default</button>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
